import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from 'react-i18next';
import api from '../utils/api';
import {
  Typography,
  Box,
  Paper,
  Button,
  Grid,
  Chip,
  Skeleton,
  Avatar,
  useTheme,
  alpha
} from '@mui/material';
import {
  FitnessCenter as GymIcon,
  Event as EventIcon,
  BookmarkBorder as BookingIcon,
  Person as PersonIcon,
  TrendingUp as TrendingUpIcon,
  AccessTime as TimeIcon,
  AcUnit as FreezeIcon
} from '@mui/icons-material';
import MUIStatCard from './ui/MUIStatCard';
import NextPlanPurchase from './NextPlanPurchase';
import PaymentStatusTracker from './PaymentStatusTracker';
import PlanFreezeRequest from './PlanFreezeRequest';
import Alert from '@mui/material/Alert';

const MemberDashboard = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useTranslation();
  const [myBookings, setMyBookings] = useState([]);
  const [stats, setStats] = useState({
    totalBookings: 0,
    upcomingBookings: 0,
    thisMonthBookings: 0,
    membershipStatus: 'active'
  });
  const [membershipInfo, setMembershipInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeFreeze, setActiveFreeze] = useState(null);
  const [freezeDialogOpen, setFreezeDialogOpen] = useState(false);

  const fetchDashboardData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Fetch data in parallel
      const [bookingsResponse, membershipResponse, freezeResponse] = await Promise.all([
        api.get('/bookings/my'),
        api.get('/users/membership-info'),
        api.get('/plan-freeze/current').catch(() => ({ data: { data: null } }))
      ]);

      const bookings = bookingsResponse.data.data || [];
      const membershipData = membershipResponse.data.data || null;
      const freezeData = freezeResponse.data.data || null;

      setActiveFreeze(freezeData);

      // Calculate stats
      const now = new Date();
      const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
      
      const upcomingBookings = bookings.filter(booking => 
        new Date(booking.startTime) > now && booking.status === 'confirmed'
      ).length;
      
      const thisMonthBookings = bookings.filter(booking => 
        new Date(booking.startTime) >= startOfMonth && booking.status === 'confirmed'
      ).length;

      setMyBookings(bookings.slice(0, 5));
      setMembershipInfo(membershipData);

      setStats({
        totalBookings: bookings.length,
        upcomingBookings,
        thisMonthBookings,
        membershipStatus: membershipData?.status || 'no_membership'
      });

    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      setError(t('dashboard:errors.failedToLoad'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  const theme = useTheme();
  
  // All users who reach this dashboard are already verified (due to DashboardPage redirect)
  // So all buttons should be enabled and no access warnings needed
  
  const quickActions = [
    {
      title: t('dashboard:member.quickActions.bookFacility.title'),
      description: t('dashboard:member.quickActions.bookFacility.description'),
      icon: <GymIcon sx={{ fontSize: 28 }} />,
      action: () => navigate('/facilities'),
      color: theme.palette.primary.main,
      bgColor: alpha(theme.palette.primary.main, 0.1)
    },
    {
      title: t('dashboard:member.quickActions.joinClasses.title'),
      description: t('dashboard:member.quickActions.joinClasses.description'),
      icon: <EventIcon sx={{ fontSize: 28 }} />,
      action: () => navigate('/classes'),
      color: theme.palette.secondary.main,
      bgColor: alpha(theme.palette.secondary.main, 0.1)
    },
    {
      title: t('dashboard:member.quickActions.myBookings.title'),
      description: t('dashboard:member.quickActions.myBookings.description'),
      icon: <BookingIcon sx={{ fontSize: 28 }} />,
      action: () => navigate('/my-bookings'),
      color: theme.palette.info.main,
      bgColor: alpha(theme.palette.info.main, 0.1)
    },
    {
      title: t('dashboard:member.quickActions.profile.title'),
      description: t('dashboard:member.quickActions.profile.description'),
      icon: <PersonIcon sx={{ fontSize: 28 }} />,
      action: () => navigate('/profile'),
      color: theme.palette.success.main,
      bgColor: alpha(theme.palette.success.main, 0.1)
    },
  ];

  const formatDateTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'confirmed': return 'success';
      case 'cancelled': return 'error';
      case 'pending': return 'warning';
      default: return 'default';
    }
  };

  if (error) {
    return (
      <Box sx={{ maxWidth: 1200, mx: 'auto', mt: 4 }}>
        <Paper
          elevation={1}
          sx={{
            p: 4,
            mt: 4,
            textAlign: 'center',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center'
          }}
        >
          <Box sx={{ fontSize: '3rem', mb: 2 }}>⚠️</Box>
          <Typography variant="h5" color="error" sx={{ mb: 1 }}>
            {t('dashboard:member.errors.genericError')}
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            {t('dashboard:member.errors.failedToLoad')}
          </Typography>
          <Button
            variant="contained"
            onClick={fetchDashboardData}
          >
            {t('dashboard:member.errors.tryAgain')}
          </Button>
        </Paper>
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', py: 2 }}>
      {/* Welcome Section */}
      <Box sx={{ 
        mb: 5, 
        background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)} 0%, ${alpha(theme.palette.secondary.main, 0.05)} 100%)`,
        borderRadius: 3,
        p: 4,
        position: 'relative',
        overflow: 'hidden'
      }}>
        <Box sx={{ 
          position: 'absolute',
          top: -10,
          right: -10,
          width: 100,
          height: 100,
          borderRadius: '50%',
          background: `linear-gradient(45deg, ${alpha(theme.palette.primary.main, 0.1)}, ${alpha(theme.palette.secondary.main, 0.1)})`,
        }} />
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Avatar
            sx={{
              width: 64,
              height: 64,
              mr: 3,
              bgcolor: theme.palette.primary.main,
              fontSize: '1.5rem'
            }}
          >
            {user?.firstName?.charAt(0) || 'U'}
          </Avatar>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5, color: 'primary.main' }}>
              {t('dashboard:member.welcome', { firstName: user?.firstName })}
            </Typography>
            <Typography variant="h6" color="text.secondary" sx={{ fontWeight: 400 }}>
              {t('dashboard:member.subtitle')}
            </Typography>
          </Box>
        </Box>
        
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 2 }}>
          <Chip 
            icon={<TrendingUpIcon />}
            label={t('dashboard:member.stats.monthlyBookings', { count: stats.thisMonthBookings })}
            variant="outlined"
            color="primary"
            size="small"
          />
          <Chip 
            label={stats.membershipStatus}
            color="success"
            size="small"
            sx={{ textTransform: 'capitalize' }}
          />
        </Box>
      </Box>

      {/* Active Freeze Alert */}
      {activeFreeze && (
        <Box sx={{ mb: 4 }}>
          <Alert
            severity="warning"
            icon={<FreezeIcon />}
            sx={{ borderRadius: 2 }}
          >
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                <strong>Your Plan is Frozen</strong>
              </Typography>
              <Grid container spacing={2} sx={{ mt: 1 }}>
                <Grid item xs={12} sm={4}>
                  <Typography variant="caption" color="text.secondary">
                    Freeze End Date
                  </Typography>
                  <Typography variant="body2" fontWeight="medium">
                    {new Date(activeFreeze.freezeEndDate).toLocaleDateString('tr-TR', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    })}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Typography variant="caption" color="text.secondary">
                    Days Remaining
                  </Typography>
                  <Typography variant="body2" fontWeight="medium" color="warning.main">
                    {Math.max(0, Math.ceil((new Date(activeFreeze.freezeEndDate) - new Date()) / (1000 * 60 * 60 * 24)))} days
                  </Typography>
                </Grid>
              </Grid>
              <Typography variant="body2" sx={{ mt: 2 }}>
                You cannot make bookings or join classes during the freeze period.
                Your plan will resume automatically on {new Date(activeFreeze.freezeEndDate).toLocaleDateString('tr-TR')}.
              </Typography>
              <Button
                size="small"
                onClick={() => navigate('/my-plan-freezes')}
                sx={{ mt: 1 }}
              >
                View Freeze History
              </Button>
            </Box>
          </Alert>
        </Box>
      )}

      {/* Current Plan Section */}
      {membershipInfo && membershipInfo.hasMembership && (
        <Box sx={{ mb: 4 }}>
          <Paper
            elevation={0}
            sx={{
              p: 4,
              borderRadius: 3,
              border: `1px solid ${theme.palette.divider}`,
              background: membershipInfo.status === 'expired' 
                ? `linear-gradient(135deg, ${alpha(theme.palette.error.main, 0.05)} 0%, ${alpha(theme.palette.error.main, 0.02)} 100%)`
                : membershipInfo.status === 'expiring_soon'
                ? `linear-gradient(135deg, ${alpha(theme.palette.warning.main, 0.05)} 0%, ${alpha(theme.palette.warning.main, 0.02)} 100%)`
                : `linear-gradient(135deg, ${alpha(theme.palette.success.main, 0.05)} 0%, ${alpha(theme.palette.success.main, 0.02)} 100%)`
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
              <Box>
                <Typography variant="h5" sx={{ fontWeight: 600, mb: 1, color: 'text.primary' }}>
                  {t('dashboard:member.membership.currentPlan', { planName: membershipInfo.plan.name })}
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                  {t('dashboard:member.membership.description', { description: membershipInfo.plan.description })}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t('dashboard:member.membership.pricing', { price: membershipInfo.plan.price, duration: membershipInfo.plan.duration })}
                </Typography>
              </Box>
              <Chip
                label={t(`dashboard:member.membership.status.${membershipInfo.status}`,
                      membershipInfo.status)}
                color={membershipInfo.status === 'expired' ? 'error' : 
                       membershipInfo.status === 'expiring_soon' ? 'warning' : 
                       'success'}
                sx={{ textTransform: 'capitalize' }}
              />
            </Box>

            {membershipInfo.remainingDays !== null && (
              <Box sx={{ mb: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    {t('dashboard:member.membership.daysRemaining')}
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {t('dashboard:member.membership.daysProgress', { remaining: membershipInfo.remainingDays > 0 ? membershipInfo.remainingDays : 0, total: membershipInfo.totalDays })}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    width: '100%',
                    height: 8,
                    borderRadius: 4,
                    backgroundColor: alpha(theme.palette.grey[400], 0.2),
                    overflow: 'hidden'
                  }}
                >
                  <Box
                    sx={{
                      height: '100%',
                      width: `${Math.min(membershipInfo.progressPercentage, 100)}%`,
                      backgroundColor: membershipInfo.status === 'expired' ? theme.palette.error.main :
                                      membershipInfo.status === 'expiring_soon' ? theme.palette.warning.main :
                                      theme.palette.success.main,
                      borderRadius: 4,
                      transition: 'width 0.3s ease-in-out'
                    }}
                  />
                </Box>
              </Box>
            )}

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
              {membershipInfo.endDate && (
                <Typography variant="body2" color="text.secondary">
                  <TimeIcon sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'middle' }} />
                  {t('dashboard:member.membership.expires', { date: new Date(membershipInfo.endDate).toLocaleDateString() })}
                </Typography>
              )}

              {!activeFreeze && membershipInfo.status === 'active' && (
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<FreezeIcon />}
                  onClick={() => setFreezeDialogOpen(true)}
                  sx={{ ml: 'auto' }}
                >
                  Request Freeze
                </Button>
              )}

              {(membershipInfo.status === 'expired' || membershipInfo.status === 'expiring_soon') && (
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => navigate('/plans')}
                  sx={{ ml: 'auto' }}
                >
                  {membershipInfo.status === 'expired' ? t('dashboard:member.membership.renewPlan') : t('dashboard:member.membership.extendPlan')}
                </Button>
              )}
            </Box>
          </Paper>
        </Box>
      )}

      {/* No Plan Section */}
      {membershipInfo && !membershipInfo.hasMembership && (
        <Box sx={{ mb: 4 }}>
          <Paper
            elevation={0}
            sx={{
              p: 4,
              borderRadius: 3,
              border: `2px dashed ${theme.palette.primary.main}`,
              bgcolor: alpha(theme.palette.primary.main, 0.02),
              textAlign: 'center'
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: 'primary.main' }}>
              {t('dashboard:member.noPlan.title')}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              {t('dashboard:member.noPlan.subtitle')}
            </Typography>
            <Button
              variant="contained"
              onClick={() => navigate('/plans')}
              sx={{ borderRadius: 2 }}
            >
              {t('dashboard:member.noPlan.choosePlan')}
            </Button>
          </Paper>
        </Box>
      )}

      {/* Next Plan Purchase Section - Only show for users with active membership */}
      {membershipInfo && membershipInfo.hasMembership && (
        <Box sx={{ mb: 4 }}>
          <NextPlanPurchase />
        </Box>
      )}
      
      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 5, width: '100%' }}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <MUIStatCard
            title={t('dashboard:member.stats.totalBookings')}
            value={stats.totalBookings}
            icon={<BookingIcon />}
            loading={loading}
            sx={{ height: '100%' }}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <MUIStatCard
            title={t('dashboard:member.stats.upcoming')}
            value={stats.upcomingBookings}
            icon={<TimeIcon />}
            loading={loading}
            sx={{ height: '100%' }}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <MUIStatCard
            title={t('dashboard:member.stats.thisMonth')}
            value={stats.thisMonthBookings}
            icon={<TrendingUpIcon />}
            trend={t('dashboard:member.stats.growth', { percent: 12 })}
            trendDirection="up"
            loading={loading}
            sx={{ height: '100%' }}
          />
        </Grid>
      </Grid>

      <Grid container spacing={4} sx={{ width: '100%' }}>
        {/* Quick Actions */}
        <Grid size={{ xs: 12, lg: 8 }}>
          <Paper 
            elevation={0}
            sx={{ 
              p: 3,
              borderRadius: 3,
              border: `1px solid ${theme.palette.divider}`,
              height: '100%'
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 3, color: 'text.primary' }}>
              {t('dashboard:member.sections.quickActions')}
            </Typography>
            {loading ? (
              <Grid container spacing={2} sx={{ width: '100%' }}>
                {[1, 2, 3, 4].map(i => (
                  <Grid size={{ xs: 6, md: 3 }} key={i}>
                    <Box sx={{ textAlign: 'center', p: 2 }}>
                      <Skeleton variant="circular" width={56} height={56} sx={{ mx: 'auto', mb: 2 }} />
                      <Skeleton width="80%" height={20} sx={{ mx: 'auto', mb: 1 }} />
                      <Skeleton width="100%" height={16} sx={{ mx: 'auto' }} />
                    </Box>
                  </Grid>
                ))}
              </Grid>
            ) : (
              <Grid container spacing={2} sx={{ width: '100%' }}>
                {quickActions.map((action, index) => (
                  <Grid size={{ xs: 6, md: 3 }} key={index}>
                    <Paper
                      elevation={0}
                      onClick={action.action}
                      sx={{ 
                        p: 3,
                        textAlign: 'center', 
                        cursor: 'pointer',
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                        border: `1px solid ${alpha(action.color, 0.2)}`,
                        borderRadius: 2,
                        bgcolor: action.bgColor,
                        '&:hover': {
                          transform: 'translateY(-4px)',
                          boxShadow: `0 8px 25px ${alpha(action.color, 0.15)}`,
                          borderColor: action.color,
                          bgcolor: alpha(action.color, 0.05)
                        }
                      }}
                    >
                      <Box 
                        sx={{ 
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: 56,
                          height: 56,
                          borderRadius: '50%',
                          bgcolor: action.bgColor,
                          color: action.color,
                          mx: 'auto',
                          mb: 2
                        }}
                      >
                        {action.icon}
                      </Box>
                      <Typography 
                        variant="subtitle1" 
                        sx={{ 
                          fontWeight: 600,
                          mb: 0.5,
                          color: action.disabled ? 'text.disabled' : 'text.primary'
                        }}
                      >
                        {action.title}
                      </Typography>
                      <Typography 
                        variant="caption" 
                        sx={{ 
                          fontSize: '0.75rem', 
                          lineHeight: 1.4,
                          color: action.disabled ? 'text.disabled' : 'text.secondary'
                        }}
                      >
                        {action.description}
                      </Typography>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            )}
          </Paper>
        </Grid>

        {/* Recent Activity */}
        <Grid size={{ xs: 12, lg: 4 }}>
          <Paper 
            elevation={0}
            sx={{ 
              p: 3,
              borderRadius: 3,
              border: `1px solid ${theme.palette.divider}`,
              height: '100%'
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary' }}>
                {t('dashboard:member.sections.recentActivity')}
              </Typography>
              <Button
                variant="text"
                size="small"
                onClick={() => navigate('/my-bookings')}
                sx={{ fontWeight: 500 }}
              >
                {t('dashboard:member.sections.viewAll')}
              </Button>
            </Box>
   

            {loading ? (
              <Box>
                {[1, 2, 3].map(i => (
                  <Box key={i} sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    py: 2,
                    borderBottom: i < 3 ? '1px solid' : 'none',
                    borderColor: 'divider'
                  }}>
                    <Skeleton variant="circular" width={40} height={40} sx={{ mr: 2 }} />
                    <Box sx={{ flex: 1 }}>
                      <Skeleton width="70%" height={18} sx={{ mb: 0.5 }} />
                      <Skeleton width="50%" height={14} />
                    </Box>
                    <Skeleton width={60} height={24} sx={{ borderRadius: 12 }} />
                </Box>
              ))}
            </Box>
            ) : myBookings.length > 0 ? (
              <Box>
                {myBookings.map((booking, index) => (
                  <Box 
                    key={booking._id} 
                    sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      py: 2,
                      borderBottom: index < myBookings.length - 1 ? '1px solid' : 'none',
                      borderColor: 'divider',
                      cursor: 'pointer',
                      borderRadius: 1,
                      transition: 'all 0.2s ease-in-out',
                      '&:hover': {
                        bgcolor: alpha(theme.palette.primary.main, 0.04),
                        transform: 'translateX(4px)'
                      }
                    }}
                    onClick={() => navigate(`/bookings/${booking._id}`)}
                  >
                    <Avatar
                      sx={{ 
                        mr: 2,
                        width: 40,
                        height: 40,
                        bgcolor: alpha(theme.palette.primary.main, 0.1),
                        color: theme.palette.primary.main
                      }}
                    >
                      <GymIcon sx={{ fontSize: 20 }} />
                    </Avatar>
                    <Box sx={{ flex: 1 }}>
                      <Typography 
                        variant="subtitle2" 
                        sx={{ 
                          fontWeight: 600,
                          mb: 0.5,
                          color: 'text.primary'
                        }}
                      >
                        {booking.facility?.name || 'Facility'}
                      </Typography>
                      <Typography 
                        variant="caption" 
                        color="text.secondary"
                        sx={{ fontSize: '0.75rem' }}
                      >
                        {formatDateTime(booking.startTime)}
                      </Typography>
                    </Box>
                    <Chip 
                      label={booking.status} 
                      color={getStatusColor(booking.status)}
                      size="small"
                      sx={{ 
                        fontWeight: 500,
                        textTransform: 'capitalize',
                        borderRadius: 12
                      }}
                    />
                  </Box>
                ))}
              </Box>
            ) : (
              <Box sx={{ textAlign: 'center', py: 6 }}>
                <Avatar
                  sx={{
                    width: 64,
                    height: 64,
                    bgcolor: alpha(theme.palette.primary.main, 0.1),
                    color: theme.palette.primary.main,
                    mx: 'auto',
                    mb: 2
                  }}
                >
                  <GymIcon sx={{ fontSize: 32 }} />
                </Avatar>
                <Typography
                  variant="h6"
                  color="text.secondary"
                  sx={{ mb: 1, fontWeight: 500 }}
                >
                  {t('dashboard:member.emptyStates.noActivity')}
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 3 }}
                >
                  {t('dashboard:member.emptyStates.activitySubtitle')}
                </Typography>
                <Button
                  variant="contained"
                  onClick={() => navigate('/facilities')}
                  sx={{ borderRadius: 2 }}
                >
                  {t('dashboard:member.emptyStates.bookNow')}
                </Button>
              </Box>
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* Payment Status Tracker Section */}
      <Box sx={{ mb: 4 }}>
        <PaymentStatusTracker />
      </Box>

      {/* Freeze Request Dialog */}
      <PlanFreezeRequest
        open={freezeDialogOpen}
        onClose={() => setFreezeDialogOpen(false)}
        onSuccess={() => {
          fetchDashboardData();
        }}
      />
    </Box>
  );
};

export default MemberDashboard;